import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  ArrowUpDown,
  MapPin,
  Maximize2,
  Clock,
  Lock,
  Loader2,
  Filter,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";
const PLACEHOLDER =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=900&q=70";

const formatINR = (n) => {
  const num = Math.round(Number(n) || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)} L`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.floor(d / 7)}w`;
  return `${Math.floor(d / 30)}mo`;
};

export default function Listings() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("q") || "");
  const [sortBy, setSortBy] = useState("newest");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    document.title = "Listings · Zumroo";
    (async () => {
      try {
        const [l, c] = await Promise.all([api.get("/listings"), api.get("/content")]);
        const list = Array.isArray(l.data) ? l.data : l.data.items || [];
        setItems(list);
        setContent(c.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let arr = items.slice();
    const q = query.trim().toLowerCase();
    if (q) {
      arr = arr.filter((l) => {
        const hay = `${l.locality || ""} ${l.city || ""} ${l.society_name || ""} ${l.address || ""} ${l.property_type || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    if (typeFilter !== "all") arr = arr.filter((l) => l.listing_type === typeFilter);
    switch (sortBy) {
      case "area-asc": arr.sort((a, b) => (a.area_sqft || 0) - (b.area_sqft || 0)); break;
      case "area-desc": arr.sort((a, b) => (b.area_sqft || 0) - (a.area_sqft || 0)); break;
      case "price-asc": arr.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case "price-desc": arr.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      default:
        arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return arr;
  }, [items, query, sortBy, typeFilter]);

  return (
    <div className="bg-[#F7F6F2] min-h-screen">
      <Navbar logoUrl={LOGO_URL} content={content || { play_store_url: "#" }} transparent={false} />

      <main className="container-z pt-20 sm:pt-24 pb-16">
        <div className="max-w-2xl">
          <div className="label-mono mb-3">Browse</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A231E]">
            Owner listings in <span className="text-[#C86646]">Pune</span>
          </h1>
          <p className="mt-3 text-base text-[#5C6B62]">
            Search by locality and sort by area or price. Tap any card to view full details and
            unlock the owner contact in the app.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-8 zcard p-4 sm:p-5 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B62]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by locality, society or address…"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#E4E1D9] bg-white focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none"
              data-testid="listings-search-input"
            />
          </div>
          <div className="relative md:w-56">
            <Filter className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B62] pointer-events-none" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full pl-11 pr-8 py-3 rounded-xl border border-[#E4E1D9] bg-white focus:border-[#1E3F33] outline-none font-medium appearance-none"
              data-testid="listings-type-select"
            >
              <option value="all">All types</option>
              <option value="Sale">For Sale</option>
              <option value="Rent">For Rent</option>
            </select>
          </div>
          <div className="relative md:w-60">
            <ArrowUpDown className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B62] pointer-events-none" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-11 pr-8 py-3 rounded-xl border border-[#E4E1D9] bg-white focus:border-[#1E3F33] outline-none font-medium appearance-none"
              data-testid="listings-sort-select"
            >
              <option value="newest">Newest first</option>
              <option value="area-desc">Area: largest first</option>
              <option value="area-asc">Area: smallest first</option>
              <option value="price-desc">Price: high to low</option>
              <option value="price-asc">Price: low to high</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-[#5C6B62]" data-testid="listings-count">
          {loading ? "Loading…" : `${filtered.length} of ${items.length} listings`}
        </div>

        {/* Grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {loading && (
            <div className="col-span-full flex items-center justify-center py-16 text-[#5C6B62]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading listings…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full zcard p-12 text-center text-[#5C6B62]">
              No listings match your filters. Try widening your search.
            </div>
          )}
          {filtered.map((l, i) => (
            <ListingCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      </main>

      <Footer logoUrl={LOGO_URL} content={content || { contact_phone: "—", contact_email: "—" }} />
    </div>
  );
}

function ListingCard({ listing: l, index: i }) {
  const img = (l.images && l.images[0]) || PLACEHOLDER;
  const isNew = l.created_at && Date.now() - new Date(l.created_at).getTime() < 7 * 86400 * 1000;
  const unlocked = !!l.is_unlocked;  // server-resolved per-viewer
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
    >
      <Link to={`/listing/${l.id}`} className="block zcard overflow-hidden" data-testid={`listing-card-${l.id}`}>
        <div className="relative">
          <img src={img} alt="" className="w-full h-44 object-cover bg-[#E4E1D9]" loading="lazy" />
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#C86646] text-white text-[10px] font-bold tracking-wider uppercase">
            {l.transaction_type || l.listing_type || "Sale"}
          </span>
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[rgba(30,63,51,0.85)] text-white text-[10px] font-semibold flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {l.city || "Pune"}
          </span>
          {isNew && (
            <span className="absolute top-12 left-3 px-2.5 py-1 rounded-full bg-[#2D6A4F] text-white text-[10px] font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Recently Added
            </span>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="font-display text-2xl font-bold text-[#1A231E] tracking-tight">
              {formatINR(l.listing_type === "Rent" ? (l.rent_amount || l.price) : l.price)}
              {l.listing_type === "Rent" && (
                <span className="text-xs text-[#5C6B62] font-medium">/mo</span>
              )}
            </div>
            {l.human_id && (
              <span className="text-[10px] font-bold tracking-wider bg-[#EAF1ED] text-[#1E3F33] px-2 py-0.5 rounded">
                {l.human_id}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-[#1A231E] font-medium truncate">
            {l.property_type}
            {l.configuration ? ` · ${l.configuration}` : (l.bedrooms ? ` · ${l.bedrooms} BHK` : "")} in {l.locality}
            {l.society_name && <span className="text-[#1E3F33] font-semibold"> · {l.society_name}</span>}
          </div>

          {/* Detail row: carpet area + furnishing + possession */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5C6B62]">
            <span className="inline-flex items-center gap-1">
              <Maximize2 className="w-3 h-3" />
              {l.carpet_area || l.area_sqft || "—"} sqft
            </span>
            {l.furnishing && (
              <span className="inline-flex items-center gap-1">· {l.furnishing}</span>
            )}
            {l.possession_status && (
              <span className="inline-flex items-center gap-1">· {l.possession_status}</span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo(l.created_at)}
            </span>
          </div>

          {/* Rent details when applicable */}
          {l.listing_type === "Rent" && l.deposit ? (
            <div className="mt-2 text-xs text-[#5C6B62]">
              Deposit: <span className="font-semibold text-[#1A231E]">{formatINR(l.deposit)}</span>
            </div>
          ) : null}

          {/* Owner preview (masked until unlocked) */}
          <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
            unlocked ? "bg-[#EAF1ED] text-[#1E3F33]" : "bg-[#FBEFE9] text-[#C86646]"
          }`} data-testid={`listing-owner-preview-${l.id}`}>
            <Lock className="w-3.5 h-3.5" />
            <span className="truncate">
              {unlocked ? "Unlocked · " : ""}
              {l.contact_name || "Owner"} · {l.contact_phone || "•••• ••••"}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
